import React from 'react';
import { useI18n, SUPPORTED_LANGUAGES, SupportedLanguage } from '../../contexts/I18nContext';
import './HeaderLanguageSelector.css';

/**
 * Two-button segmented toggle for the top bar.
 *
 * Renders each supported language as a flag-pill with its short code,
 * highlighting the active one. Clicking the inactive pill switches.
 * Designed to match the translucent-white pill look of ApiStatusIndicator
 * on the dark header gradient.
 */
export const HeaderLanguageSelector: React.FC = () => {
  const { language, setLanguage, t, availableLanguages } = useI18n();

  const handleSelect = (code: SupportedLanguage) => {
    if (code !== language) setLanguage(code);
  };

  const shortLabel = (code: SupportedLanguage): string => {
    // en-GB -> EN, fr -> FR. Kept in code so it doesn't need translating.
    return code.split('-')[0].toUpperCase();
  };

  return (
    <div
      className="header-language-selector"
      role="radiogroup"
      aria-label={t('language.selectLanguage')}
    >
      {availableLanguages.map((lang) => {
        const isActive = lang.code === language;
        const info = SUPPORTED_LANGUAGES[lang.code];
        return (
          <button
            key={lang.code}
            type="button"
            role="radio"
            aria-checked={isActive}
            className={`header-language-option${isActive ? ' active' : ''}`}
            onClick={() => handleSelect(lang.code)}
            title={info.nativeName}
          >
            <span className="header-language-flag" aria-hidden="true">{info.flag}</span>
            <span className="header-language-code">{shortLabel(lang.code)}</span>
          </button>
        );
      })}
    </div>
  );
};

export default HeaderLanguageSelector;
